<template>
    <div v-if="game" class="main-sections text-center">
        <h2 class="h2 font-weight-bold">Game Summary</h2>
        <p></p>

        <div class="summary-content">
            <!-- Difficulty row with icon and color -->
            <div class="summary-row">
                <div :class="['icon', 'icon-shape', difficultyColor, 'rounded-circle', 'text-white']">
                    <i :class="difficultyIcon"></i>
                </div>
                <h3 :class="['h3', 'ml-3']">{{ difficultyText }}</h3>
            </div>

            <!-- Score row with icon -->
            <div class="summary-row">
                <div class="icon icon-shape bg-gradient-default rounded-circle text-white">
                    <i class="ni ni-check-bold"></i>
                </div>
                <h3 class="ml-3"> {{ guessedPlayers }}/{{ totalPlayers }}</h3>
            </div>

            <!-- Time row with icon -->
            <div class="summary-row">
                <div class="icon icon-shape bg-gradient-primary rounded-circle text-white">
                    <i class="ni ni-watch-time"></i>
                </div>
                <h3 class="ml-3"> {{ gameDuration }}</h3>
            </div>


            <b-button type="button" id="popover-target" class="btn btn-info summary-row" @click="shareResult"
                ref="popoverButton">
                <div class="icon icon-shape text-white">
                    <i class="fa fa-share-alt"></i>
                </div>
                Share result
            </b-button>

            <b-popover target="popover-target" triggers="manual" ref="popover" :show.sync="showPopover">
                Copied to clipboard!
            </b-popover>

            <b-button type="button" class="btn btn-success summary-row" @click="playAgain" ref="popoverButton">
                <div class="icon icon-shape text-white">
                    <i class="fa fa-refresh"></i>
                </div>
                Play Again
            </b-button>

            <b-button v-if="game && hiscoreName === ''" type="button" class="btn btn-default summary-row"
                @click="showHiscoreModal">
                <div class="icon icon-shape text-white">
                    <i class="fa fa-list"></i>
                </div>
                Submit to Hiscores
            </b-button>

            <b-modal ref="hiscoreModal" v-model="showingHiscoreModal" title="Submit to Hiscores"
                content-class="custom-modal-content" modal-class="custom-modal">
                <b-form @submit.prevent="submitHiscore">
                    <b-form-group label="Name">
                        <b-form-input v-model="hiscoreNameUserInput" type="text" placeholder="Enter your name" required
                            minlength="3"></b-form-input>
                    </b-form-group>

                    <b-form-group label="Country">

                        <country-dropdown @onSelect="onSelect" :preferredCountries="['NL', 'US', 'GB']"
                            :defaultCountryByAreaCode="31" :immediateCallSelectEvent="true" :enabledFlags="true"
                            :enabledCountryCode="false" :showNameInput="true"
                            :dropdownOptions="{ disabledDialCode: true }">
                        </country-dropdown>
                    </b-form-group>
                </b-form>
                <template #modal-footer>
                    <b-button type="button" variant="secondary" @click="showingHiscoreModal = false">Cancel</b-button>
                    <b-button type="submit"
                        :disabled="!hiscoreNameUserInput || hiscoreNameUserInput.length < 3 || hiscoreNameUserInput.length > 15"
                        variant="primary" @click="submitHiscore">Submit</b-button>
                </template>
            </b-modal>


            <b-button v-if="game && hiscoreName !== ''" type="button" class="btn btn-default summary-row"
                @click="viewHiscores">
                <div class="icon icon-shape text-white">
                    <i class="fa fa-list"></i>
                </div>
                Submitted as {{ hiscoreName }} - View Hiscores
            </b-button>
        </div>

        <div class="game-elements">
            <div v-for="gamePlayer in game.players" :key="gamePlayer.player.id" class="game-player-row">
                <div class="scrollable-container">
                    <div class="player-card-container">
                        <PlayerCard :selectedGamePlayer="gamePlayer" />
                    </div>

                    <div class="player-history-container">
                        <PlayerHistory :transfers="gamePlayer.player.transfers" />
                    </div>
                </div>

                <div class="status-icon-container">
                    <!-- Status Icon -->
                    <div
                        :class="['icon', 'icon-shape', getStatusColor(gamePlayer.status), 'rounded-circle', 'text-white']">
                        <i :class="getStatusIcon(gamePlayer.status)"></i>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>


<script>
import axios from 'axios';
import PlayerHistory from './components/PlayerHistory.vue';
import PlayerCard from './components/PlayerCard.vue';

export default {
    components: {
        PlayerHistory,
        PlayerCard
    },
    props: {
        gameUuid: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            game: null,
            showPopover: false,
            showingHiscoreModal: false,
            hiscoreName: '',
            hiscoreNameUserInput: '',
            hiscoreCountryCode: '',
            result: null
        };
    },
    computed: {
        guessedPlayers() {
            if (!this.game || !this.game.players) {
                return 0;
            }
            // Access the player object nested within GamePlayer and check its status
            return this.game.players.filter(gamePlayer => gamePlayer.status === 'GUESSED').length;
        },
        totalPlayers() {
            if (!this.game || !this.game.players) {
                return 0;
            }
            return this.game.players.length;
        },
        gameDuration() {
            if (!this.game || !this.game.start_time || !this.game.end_time) {
                return 'N/A';
            }

            const startTime = new Date(this.game.start_time);
            const endTime = new Date(this.game.end_time);

            const difference = endTime - startTime; // difference in milliseconds
            const minutes = Math.floor(difference / 60000);
            const seconds = ((difference % 60000) / 1000).toFixed(0);

            return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
        },
        difficultyColor() {
            if (this.game && this.game.difficulty) {
                return {
                    'EASY': 'bg-gradient-success',
                    'NORMAL': 'bg-gradient-warning',
                    'HARD': 'bg-gradient-danger'
                }[this.game.difficulty];
            }
            return 'bg-gradient-default';
        },
        difficultyIcon() {
            if (this.game && this.game.difficulty) {
                return {
                    'EASY': 'ni ni-support-16',
                    'NORMAL': 'ni ni-send',
                    'HARD': 'ni ni-spaceship'
                }[this.game.difficulty];
            }
            return 'ni ni-unknown';
        },
        difficultyText() {
            if (this.game && this.game.difficulty) {
                return {
                    'EASY': 'Easy',
                    'NORMAL': 'Normal',
                    'HARD': 'Hard'
                }[this.game.difficulty];
            }
            return 'Unknown';
        },
        difficultyTextColor() {
            if (this.game && this.game.difficulty) {
                return {
                    'EASY': 'text-success',
                    'NORMAL': 'text-warning',
                    'HARD': 'text-danger'
                }[this.game.difficulty];
            }
            return 'text-default';
        },
        gameHiscoreName() {
            return this.game && this.game.hiscore_entry
                ? this.game.hiscore_entry.name
                : '';
        }
    },

    async created() {
        this.getGame();
    },
    methods: {
        async getGame() {
            try {
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
                const response = await axios.get(`${apiBaseUrl}/api/get_game/${this.gameUuid}/`);

                if (response.status === 200) {
                    this.game = response.data;

                    if (this.game.hiscore_entry) {
                        this.hiscoreName = this.game.hiscore_entry.name;
                    }
                } else {
                    console.error('Failed to retrieve game:', response);
                }
            } catch (error) {
                console.error('An error occurred while retrieving the game:', error);
            }
        },
        getStatusColor(status) {
            return status === 'GUESSED' ? 'bg-gradient-success' : 'bg-gradient-danger';
        },
        getStatusIcon(status) {
            return status === 'GUESSED' ? 'ni ni-check-bold' : 'ni ni-fat-remove';
        },
        playAgain() {
            // Check if the game object and its difficulty property exist
            const difficulty = this.game && this.game.difficulty ? this.game.difficulty : null;

            // Prepare the router path and query parameters
            const path = '/start-game';
            let query = difficulty ? { difficulty } : {};

            // Use Vue Router to navigate to the start-game page with or without difficulty
            this.$router.push({ path, query });
            if (typeof gtag !== "undefined") {
                gtag('event', 'playAgain', {
                    uuid: this.gameUuid
                });
            }
        },
        shareResult() {
            let resultString = `I scored ${this.guessedPlayers}/${this.totalPlayers} in ${this.gameDuration} in Football Transfer Quiz ${this.difficultyText} mode. \nCan you beat me?\n`;
            let gameSequence = this.game.players.map(player => player.status === 'GUESSED' ? '✅' : '❌').join('');
            const websiteBaseUrl = process.env.VUE_APP_WEBSITE_BASE_URL;

            if (typeof gtag !== "undefined") {
                gtag('event', 'sharedResult', {
                    uuid: this.gameUuid
                });
            }

            let challengeUrl = `${websiteBaseUrl}/#/start-game?challenge=${this.gameUuid}`
            resultString += gameSequence;
            resultString += "\n"
            resultString += challengeUrl

            // Copy resultString to clipboard
            navigator.clipboard.writeText(resultString).then(() => {
                // Show the popover with the success message
                this.showPopover = true;
                // Set a timeout to hide the popover after 2 seconds
                setTimeout(() => {
                    this.showPopover = false;
                }, 2000);
            }).catch(err => {
                console.error('Could not copy text:', err);
            });
        },
        showHiscoreModal() {
            this.showingHiscoreModal = true;

            if (typeof gtag !== "undefined") {
                gtag('event', 'submitHiscoreButtonClicked', {
                    uuid: this.gameUuid
                });
            }
        },
        async submitHiscore() {
            this.hiscoreName = this.hiscoreNameUserInput
            this.hiscoreNameUserInput = ''

            try {
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
                const url = `${apiBaseUrl}/api/game/${this.game.uuid}/hiscore/`;
                const data = {
                    name: this.hiscoreName,
                    countrycode: this.hiscoreCountryCode
                };

                if (typeof gtag !== "undefined") {
                    gtag('event', 'submitHiscoreSubmitted', {
                        uuid: this.gameUuid,
                        name: this.hiscoreName,
                        countrycode: this.hiscoreCountryCode
                    });
                }

                const response = await axios.post(url, data);
                console.log('Hiscore created:', response.data);
                this.getGame()
            } catch (error) {
                // Handle error (e.g., display error message)
                console.error('Error creating hiscore:', error.response.data || error);
            }

            this.showingHiscoreModal = false; // Close the modal
        },
        onSelect({ name, iso2, dialCode }) {
            this.hiscoreCountryCode = iso2
        },
        viewHiscores() {
            this.$router.push({ path: '/', hash: 'hiscores' });
            
            
            // Log the event with Google Analytics
            if (typeof gtag !== "undefined") {
                gtag('event', 'viewHiscores', {
                    uuid: this.gameUuid,
                });
            }
        },
    },
};
</script>

<style scoped>
.header {
    width: 100%;
    height: 90px;
    margin-bottom: 20px;
}

.main-sections {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    flex-grow: 1;
}

.summary-content {
    margin-left: 5%;
    margin-right: 5%;
    display: flex;
    flex-wrap: wrap;
    /* Enable wrapping */
    justify-content: center;
    /* Center items horizontally */
    padding-bottom: 3rem;
}

.summary-row {
    flex: 1 1 30%;
    /* Adjust the flex-basis to a percentage that works with your design */
    display: flex;
    align-items: center;
    padding: 0 1rem;
    margin-bottom: 1rem;
    /* Add some space between wrapped items */
}

@media (max-width: 768px) {
    .summary-row {
        flex-basis: 30%;
        margin-right: 0.5rem;
        /* On smaller screens, make each item take half the width */
    }
}

@media (max-width: 480px) {
    .summary-row {
        flex-basis: 45%;
        margin-right: 0.5rem;

        /* On very small screens, stack items vertically */
    }
}


.game-elements {
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-items: center;

}

.game-player-row {
    display: flex;
    justify-content: space-between;
    /* Adjusted to space-between to put the icon at the end */
    align-items: center;
    max-width: 100rem;
    width: 100vw;
}

.scrollable-container {
    display: flex;
    overflow-x: auto;
    align-items: center;
    justify-content: flex-start;
    /* Align items to the start of the container */
    margin-right: 1rem;
    /* Adjust space between scrollable content and icon */
    gap: 1em;
}

/* Last child margin fix */
.player-history-container:last-child {
    margin-right: 0;
    /* Removes margin from the last element */
}

.status-icon-container {
    flex-shrink: 0;
    margin-right: 1em;
    /* Prevents the icon container from shrinking */
}


.player-card-container {
    width: 12em;
    height: 18em;
    max-width: 20vw;
    max-height: 30vw;
    flex-shrink: 0;
    padding: 0.5em;
    /* Prevents the icon container from shrinking */

}
</style>